import React, { useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
} from '@mui/material'
import { calculatePaymentSchedule } from './calculatePaymentSchedule'
import { calculateCSAPaymentSchedule } from './calculateCSAPaymentSchedule'
import { useParams } from 'react-router-dom'
import { formatCurrency } from '../DealsKanban/aggregateStageTotals'
import dayjs from 'dayjs'
import NoDataDefault from 'components/ui/no-data-default'
import { useDealData } from 'api/aws/deals/useDealData'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { BoxScroller } from 'styles/elements/styleElements'

type PaymentRow = {
  month: string
  year: number
  paymentAmount: number | string
  grossProfit: number | string
  mediaSpend: number | string
  grossMargin: number | string
  productCount: number
  activeDays: number
  productsAssociated: string[] | string
}

const PaymentSchedule = () => {
  const { PK, SK } = useParams()
  const { pinned } = useLayoutContext()
  const dealId = SK?.split('DEAL_')?.[1]

  const { deal, productRows } = useDealData(PK, SK, dealId)

  const theme = useTheme()

  const dateYear = (dateStr?: string | null): number | null => {
    return dateStr ? dayjs(dateStr).year() : null
  }

  const csaTypes = ['CSA', 'RCSA'] as const
  const isCSA = !!deal?.type && csaTypes.includes(deal.type as any)
  // Determine which calculation function to use based on contract type
  const dealYear = dateYear(deal?.startDate)
  const isAfter2024 = (dealYear ?? 0) > 2024
  const paymentSchedule: PaymentRow[] = useMemo(() => {
    const raw = isCSA
      ? calculateCSAPaymentSchedule(deal!, productRows)
      : calculatePaymentSchedule(productRows, Number(deal?.implementationFee) || 0)
    // If your calculators already return PaymentRow[], you can drop this map.
    return (raw ?? []).map((r: any) => ({
      month: String(r.month ?? ''),
      year: typeof r.year === 'number' ? r.year : Number(r.year ?? 0),
      paymentAmount: typeof r.paymentAmount === 'number' ? r.paymentAmount : Number(r.paymentAmount ?? 0),
      grossProfit: Number(r.grossProfit ?? 0),
      mediaSpend: Number(r.mediaSpend ?? 0),
      grossMargin: Number(r.grossMargin ?? 0),
      productCount: Number(r.productCount ?? 0),
      activeDays: Number(r.activeDays ?? 0),
      productsAssociated: Array.isArray(r.productsAssociated)
        ? r.productsAssociated
        : String(r.productsAssociated ?? ''),
    }))
  }, [isCSA, deal, productRows])
  const totalContractAmountNum = paymentSchedule.reduce<number>((sum, item) => sum + Number(item.paymentAmount || 0), 0)
  const totalContractAmount = totalContractAmountNum.toFixed(2)
  const totalGrossProfit = paymentSchedule
    .reduce<number>((sum, item) => sum + Number(item.grossProfit || 0), 0)
    .toFixed(2)
  const totalMediaSpendNum = paymentSchedule.reduce<number>((sum, item) => sum + Number(item.mediaSpend || 0), 0)
  const totalMediaSpend = totalMediaSpendNum.toFixed(2)
  // Gross profit and media spend sum to revenue, so the deal-level margin is
  // recomputed off the totals rather than averaging the monthly percentages.
  const totalRevenue = Number(totalGrossProfit) + totalMediaSpendNum
  const totalGrossMargin = totalRevenue > 0 ? ((Number(totalGrossProfit) / totalRevenue) * 100).toFixed(2) : '0.00'

  if (!productRows?.length) {
    return (
      <NoDataDefault defaultMessage={!productRows?.length ? 'Please complete contract for payment schedule' : null} />
    )
  }

  return (
    // <Box sx={{ marginTop: 4, p: 2 }}>
    <BoxScroller
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start', // Align items to the top to prevent content from being cut off
        height: pinned ? 'calc(100vh - 185px)' : 'calc(100vh - 122px)', // Ensure the content fits within the viewport
        transition: 'height 0.3s ease',
        overflowY: 'auto', // Enable vertical scrolling if needed
        padding: 2, // Add some padding to prevent content from touching edges
      }}
    >
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650, borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                Month
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                Year
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                Payment Amount ($)
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                Gross Profit ($)
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                Media Spend ($)
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                Gross Margin (%)
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                Products Associated
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(paymentSchedule) && paymentSchedule?.length > 0
              ? paymentSchedule.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      border: '1px solid rgba(224, 224, 224, 1)',
                      backgroundColor: 'transparent',
                    }}
                  >
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                      {deal.type === 'CSA' || deal.type === 'RCSA' ? `Payment ${index + 1}` : item.month}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                      {deal.type === 'CSA' || deal.type === 'RCSA' ? item.year : item.year}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                      {formatCurrency(item.paymentAmount)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                      {formatCurrency(item.grossProfit)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                      {formatCurrency(item.mediaSpend)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                      {Number(item.grossMargin).toFixed(2)}%
                    </TableCell>
                    <TableCell sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                      {Array.isArray(item.productsAssociated)
                        ? item.productsAssociated.join(', ')
                        : item.productsAssociated}
                    </TableCell>
                  </TableRow>
                ))
              : null}
            <TableRow
              sx={{
                border: '1px solid rgba(224, 224, 224, 1)',
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
              }}
            >
              <TableCell
                colSpan={2}
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                Deal Totals
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                ${totalContractAmount}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                ${totalGrossProfit}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                ${totalMediaSpend}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  border: '1px solid white',
                }}
              >
                {totalGrossMargin}%
              </TableCell>
              <TableCell
                sx={{
                  border: '1px solid white',
                }}
              />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </BoxScroller>
  )
}

export default PaymentSchedule
